// BookDetails.jsx
import React, { useContext, useRef, useState } from 'react';
import closeIcon from '../images/close-line.svg';
import bookCover from '../images/empty-book.svg';
import calendarIcon from '../images/calendar-line.svg';
import clockIcon from '../images/clock-line.svg';
import speedIcon from '../images/stats-line.svg';
import sessionIcon from '../images/stats-line.svg';
import quoteIcon from '../images/chat-line.svg';
import summaryIcon from '../images/edit-line.svg';
import pagesIcon from '../images/file-line.svg';
import closeButton from '../images/close-line-white.svg';
import './BookDetails.css';
import moment from 'moment';
import 'moment/locale/sv';
import { prettySecondsToTime, updateProtocol } from '../utils/functions';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import EditBook from './EditBook';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import BarChart from './BarChart';
import { useTranslation } from '../i18n';
import BookProgress from './BookProgress';
import { PersistentContext } from './PersistentContext';
import BookInfo from './BookInfo';

const BookDetails = ({
  user,
  book,
  buttonClass,
  fetchBooks,
  fetchUserData,
  showNotification,
  updateCurrentlyReading,
}) => {
  const [dialogIsOpen, setDialogOpen] = useState(false);
  const [moveModalIsOpen, setMoveModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [deletingBook, setDeletingBook] = useState(false);
  const [moving, setMoving] = useState(false);
  const { globalState, setGlobalState } = useContext(PersistentContext);
  const dialogRef = useRef(null);
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();

  const handleMoveBook = async (status, list) => {
    setMoving(true);
    try {
      const q = query(
        collection(db, 'UserBooks'),
        where('bookID', '==', book.bookID),
        where('userID', '==', book.userID),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const docRef = doc.ref;

        const updateData = {
          status: status,
        };

        // Update read count
        if (status === 'read') {
          let numberOfReads = 0;
          if (doc.data().numberOfReads) {
            numberOfReads = doc.data().numberOfReads;
          }
          updateData.numberOfReads = numberOfReads + 1;
        }

        // Reset currently read pages if book was moved from read list
        if (doc.data().status === 'read') {
          updateData.pages = 0;
        }

        // Reset currentlyReading
        if (doc.data().status === 'reading') {
          updateCurrentlyReading(book, user, false);
        }

        // Update currentlyReading if status reading and book has pages
        if (status === 'reading' && !isNaN(book.number_of_pages)) {
          updateCurrentlyReading(book, user, true);
        }

        await updateDoc(docRef, updateData);
        fetchBooks();
        showNotification(`${book.title} ${t.movedto} ${list}!`);
      }
    } catch (error) {
      console.error('Error updating UserBook status: ', error);
    } finally {
      setMoving(false);
      closeMoveModal();
    }
  };

  const openDialog = () => {
    if (dialogRef.current) {
      setDialogOpen(true);
      dialogRef.current.showModal();
    }
  };

  const closeDialog = () => {
    if (dialogRef.current) {
      setDialogOpen(false);
      setDeleteModalIsOpen(false);
      dialogRef.current.close();
    }
  };

  function openMoveModal() {
    setMoveModalIsOpen(true);
  }

  function closeMoveModal() {
    setMoveModalIsOpen(false);
  }

  function openDeleteModel() {
    setDeleteModalIsOpen(true);
  }

  function closeDeleteModal() {
    setDeleteModalIsOpen(false);
  }

  const deleteBook = async () => {
    try {
      setDeletingBook(true);
      await deleteDoc(doc(db, 'UserBooks', book.id)).then(() => fetchBooks());
      showNotification(`${book.title} ${t.deleted}!`);

      // Also update User if book was currentlyReading
      const userRef = doc(db, 'Users', book.userID);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (
          userData.currentlyReading &&
          userData.currentlyReading.bookID === book.bookID
        ) {
          await updateDoc(userRef, { currentlyReading: {} }).then(() => {
            setGlobalState({
              ...globalState,
              isRunning: false,
              activeSession: false,
              duration: 0,
              sessionDuration: 0,
              startTime: null,
              endPage: 0,
            });
            fetchUserData(userData.email, false, 'deleteBook, BookDetails.js');
          });
        }
      }
    } catch (err) {
      console.error('Failed to delete the book. Please try again.');
    } finally {
      setDeletingBook(false);
      closeDeleteModal();
      closeDialog();
    }
  };

  const [isEditing, setIsEditing] = useState(false);

  function toggleEdit() {
    setIsEditing(!isEditing);
  }

  const sessions = book.sessions || [];
  const quotes = sessions.filter((session) => session.quote !== '');
  const summaries = sessions.filter((session) => session.summary !== '');

  return [
    <button className={buttonClass} onClick={openDialog} key="button">
      {t.details}
    </button>,
    <dialog ref={dialogRef} key="dialog">
      {dialogIsOpen && (
        <div className="fullscreen-container" tabIndex={-1}>
          <button className="fullscreen-close" onClick={closeDialog}>
            <img src={closeIcon} width={42} height={42} alt={t.close} />
            {t.close}
          </button>

          <div className="card-wrapper">
            <div className="card">
              <div className="card-medium">
                <img
                  src={updateProtocol(book.cover_url[0]) || bookCover}
                  alt={book.title}
                  height={120}
                  width={80}
                  className="card-medium-image"
                />
                <BookInfo book={book} t={t} />
              </div>
              <BookProgress book={book} />
            </div>

            <div className="card">
              <div className="stats-grid">
                {book.dateStarted ? (
                  <div className="flex-col gap-25">
                    <div className="pt-65 fs-s">
                      {book.dateFinished ? t.dateCompleted : t.dateStarted}
                    </div>
                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={calendarIcon}
                        width={20}
                        height={20}
                        alt="Calendar icon"
                      />
                      {book.dateFinished
                        ? moment(book.dateStarted.toDate())
                            .locale(lang)
                            .format('D MMM') +
                          `—` +
                          moment(book.dateFinished.toDate())
                            .locale(lang)
                            .format('D MMM, YYYY')
                        : moment(book.dateStarted.toDate())
                            .locale(lang)
                            .format('D MMM, YYYY')}
                    </div>
                  </div>
                ) : (
                  <div className="flex-col gap-25">
                    <div className="pt-65 fs-s">{t.dateStarted}</div>
                    <div className="date fs-s pt-65 nowrap">
                      <img
                        className="icon"
                        src={calendarIcon}
                        width={20}
                        height={20}
                        alt="Calendar icon"
                      />
                      {t.notStarted}
                    </div>
                  </div>
                )}
                <div className="flex-col gap-25">
                  <div className="pt-65 fs-s">{t.pagesRead}</div>
                  <div className="date fs-s pt-65 nowrap">
                    <img
                      className="icon"
                      src={pagesIcon}
                      width={20}
                      height={20}
                      alt="Pages icon"
                    />
                    {book.pages || 0}{' '}
                    {book.pages !== 1 ? t.pagesLow : t.pageLow}
                  </div>
                </div>
                <div className="flex-col gap-25">
                  <div className="pt-65 fs-s">{t.timeRead}</div>
                  <div className="date fs-s pt-65 nowrap">
                    <img
                      className="icon"
                      src={clockIcon}
                      width={20}
                      height={20}
                      alt="Date icon"
                    />
                    {prettySecondsToTime(book.time || 0)}
                  </div>
                </div>

                <div className="flex-col gap-25">
                  <div className="pt-65 fs-s">{t.selectpagespeed}</div>
                  <div className="date fs-s pt-65 nowrap">
                    <img
                      className="icon"
                      src={speedIcon}
                      width={20}
                      height={20}
                      alt="Reading speed icon"
                    />
                    {book.pages > 0 && book.time > 0
                      ? Math.round((book.pages / book.time) * 60 * 60)
                      : 0}{' '}
                    {t.pagesperhour}
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <Tabs>
                <div className="stats">
                  <TabList className="tabs-pill">
                    <Tab className="tab-pill">
                      <div className="date fs-s pt-65 nowrap">
                        <img
                          className="icon"
                          src={sessionIcon}
                          width={20}
                          height={20}
                          alt="Session icon"
                        />
                        {sessions.length || 0}{' '}
                        {sessions.length !== 1 ? t.sessionsLow : t.sessionLow}
                      </div>
                    </Tab>
                    <Tab className="tab-pill">
                      <div className="date fs-s pt-65 nowrap">
                        <img
                          className="icon"
                          src={quoteIcon}
                          width={20}
                          height={20}
                          alt="Quote icon"
                        />
                        {quotes.length || 0}{' '}
                        {quotes.length !== 1 ? t.quotesLow : t.quoteLow}
                      </div>
                    </Tab>
                    <Tab className="tab-pill">
                      <div className="date fs-s pt-65 nowrap">
                        <img
                          className="icon"
                          src={summaryIcon}
                          width={20}
                          height={20}
                          alt="Summary icon"
                        />
                        {summaries.length || 0}{' '}
                        {summaries.length !== 1 ? t.summariesLow : t.summaryLow}
                      </div>
                    </Tab>
                  </TabList>
                </div>

                <TabPanel>
                  <div
                    className={`tabpanel-pill tabpanel-barchart ${sessions.length === 0 ? 'nodata' : undefined}`}
                  >
                    {sessions.length > 0 ? (
                      <BarChart sessions={sessions} />
                    ) : (
                      t.nosessions
                    )}
                  </div>
                </TabPanel>
                <TabPanel>
                  <div className="tabpanel-pill">
                    {quotes.length > 0
                      ? quotes.map((quote) => {
                          return (
                            <div key={quote.date}>
                              <div>{`—${quote.quote}`}</div>
                              <div className="pt-65">
                                {moment(quote.date.toDate())
                                  .locale(lang)
                                  .format('D MMM, YYYY')}
                              </div>
                            </div>
                          );
                        })
                      : t.noquotes}
                  </div>
                </TabPanel>
                <TabPanel>
                  <div className="tabpanel-pill">
                    {summaries.length > 0
                      ? summaries.map((summary) => {
                          return (
                            <div key={summary.date}>
                              <div>{summary.summary}</div>
                              <div className="pt-65">
                                {moment(summary.date.toDate())
                                  .locale(lang)
                                  .format('D MMM, YYYY')}
                              </div>
                            </div>
                          );
                        })
                      : t.nosummaries}
                  </div>
                </TabPanel>
              </Tabs>
            </div>

            {book.review && (
              <div className="card">
                <div className="pt-65 fs-s">{t.review}</div>
                <div>{book.review}</div>
              </div>
            )}
          </div>

          <div className="btn-group-wrapper">
            <div className="btn-group btn-group-two-col">
              <button
                className="btn btn-secondary w-100"
                onClick={() => toggleEdit()}
              >
                {t.editdetails}
              </button>
              <button
                className="btn btn-primary w-100"
                onClick={() => openMoveModal()}
              >
                {t.movetolist}
              </button>
            </div>
            <button
              className="btn btn-borderless w-100"
              onClick={() => openDeleteModel()}
            >
              {t.deletebook}
            </button>
          </div>
        </div>
      )}
      {deleteModalIsOpen && (
        <div className="overlay">
          <div className="buttons-wrapper">
            <div className="buttons-header">
              <span>{t.confirmdeletebook}</span>
              <button
                className="btn-icon"
                onClick={closeDeleteModal}
                disabled={deletingBook}
              >
                <img
                  className="icon"
                  src={closeButton}
                  width={24}
                  height={24}
                  alt="Close icon"
                />
              </button>
            </div>
            <div className="separator"></div>
            <div className="buttons">
              <button
                className="btn btn-secondary btn-danger"
                onClick={() => deleteBook()}
                disabled={deletingBook}
              >
                {t.deletebook}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => closeDeleteModal()}
                disabled={deletingBook}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
      {moveModalIsOpen && (
        <div className="overlay">
          <div className="buttons-wrapper">
            <div className="buttons-header">
              <span>{t.movetolist}</span>
              <button
                className="btn-icon"
                onClick={closeMoveModal}
                disabled={moving}
              >
                <img
                  className="icon"
                  src={closeButton}
                  width={24}
                  height={24}
                  alt="Close icon"
                />
              </button>
            </div>
            <div className="separator"></div>
            <div className="buttons">
              {book.status !== 'reading' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleMoveBook('reading', t.currentlyreading)}
                  disabled={moving}
                >
                  {t.currentlyreading}
                </button>
              )}
              {book.status !== 'wantToRead' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleMoveBook('wantToRead', t.wanttoread)}
                  disabled={moving}
                >
                  {t.wanttoread}
                </button>
              )}
              {book.status !== 'read' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleMoveBook('read', t.haveread)}
                  disabled={moving}
                >
                  {t.haveread}
                </button>
              )}
              {book.status === 'reading' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => handleMoveBook('dnf', t.dnf)}
                  disabled={moving}
                >
                  {t.dnf}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {isEditing && (
        <EditBook
          book={book}
          backText={t.backtodetails}
          buttonText={t.updatebook}
          fetchBooks={fetchBooks}
          toggleEdit={toggleEdit}
          showNotification={showNotification}
          user={user}
          updateCurrentlyReading={updateCurrentlyReading}
        />
      )}
    </dialog>,
  ];
};

export default BookDetails;
